import React, { useState } from 'react';
import { Play, Quote, Loader2, FileText, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useTestimonials } from '../hooks/useApi';
import { absoluteUrl } from '../lib/api';

const youtubeEmbed = (url) => {
  try {
    const u = new URL(url);
    let id = '';
    if (u.hostname.includes('youtu.be')) id = u.pathname.slice(1);
    else if (u.searchParams.get('v')) id = u.searchParams.get('v');
    else if (u.pathname.includes('/embed/')) id = u.pathname.split('/embed/')[1];
    return id ? `https://www.youtube.com/embed/${id}?rel=0&playsinline=1&autoplay=1` : url;
  } catch {
    return url;
  }
};

const VideoCard = ({ t, onPlay }) => (
  <article
    className="group relative bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-lg hover:shadow-2xl btn-transition hover:-translate-y-1"
    data-testid={`video-testimonial-card-${t.id}`}
  >
    <button
      onClick={() => onPlay(t)}
      className="relative block w-full aspect-video overflow-hidden"
      data-testid={`video-testimonial-play-${t.id}`}
      aria-label={`Play testimonial from ${t.name}`}
    >
      <img
        src={absoluteUrl(t.thumbnail_url) || 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&q=80'}
        alt={`${t.name} testimonial thumbnail`}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <span className="absolute inset-0 bg-gradient-to-t from-[#050F2E]/80 via-[#050F2E]/10 to-transparent" />
      <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#0A2A63]">
        Video testimonial
      </span>
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="h-16 w-16 rounded-full bg-white/95 shadow-xl flex items-center justify-center group-hover:scale-110 transition-transform">
          <Play className="h-7 w-7 text-[#0A2A63] ml-1" fill="currentColor" />
        </span>
      </span>
    </button>
    <div className="p-6">
      {t.quote && (
        <p className="text-slate-700 leading-relaxed mb-4 flex gap-2">
          <Quote className="h-4 w-4 text-[#FF4E50] flex-shrink-0 mt-1" />
          <span>&ldquo;{t.quote}&rdquo;</span>
        </p>
      )}
      <div className="pt-4 border-t border-slate-100">
        <p className="font-bold text-[#050F2E]">{t.name}</p>
        <p className="text-xs text-slate-500">{[t.title, t.company].filter(Boolean).join(' · ')}</p>
      </div>
    </div>
  </article>
);

const VideoModal = ({ t, onClose }) => {
  const [showTranscript, setShowTranscript] = useState(false);
  if (!t) return null;
  return (
    <Dialog open={!!t} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-[#050F2E] border-0" data-testid="video-testimonial-modal">
        <DialogTitle className="sr-only">{t.name} — video testimonial</DialogTitle>
        <div className="relative w-full aspect-video bg-black">
          {t.video_type === 'youtube' ? (
            <iframe
              className="absolute inset-0 w-full h-full"
              src={youtubeEmbed(t.video_url)}
              title={`${t.name} testimonial`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video
              className="absolute inset-0 w-full h-full"
              src={absoluteUrl(t.video_url)}
              poster={absoluteUrl(t.thumbnail_url)}
              controls
              playsInline
              data-testid="video-testimonial-player"
            >
              {t.transcript && (
                <track kind="captions" srcLang="en" label="English" />
              )}
            </video>
          )}
        </div>
        <div className="p-6 text-white">
          <p className="text-lg font-black">{t.name}</p>
          <p className="text-sm text-slate-300">{[t.title, t.company].filter(Boolean).join(' · ')}</p>
          {t.transcript && (
            <div className="mt-4">
              <button
                onClick={() => setShowTranscript((v) => !v)}
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FF4E50]"
                data-testid="video-transcript-toggle"
              >
                <FileText className="h-3.5 w-3.5" /> {showTranscript ? 'Hide transcript' : 'Show transcript'}
              </button>
              {showTranscript && (
                <p className="mt-3 text-sm text-slate-300 leading-relaxed border-l-2 border-white/20 pl-4">{t.transcript}</p>
              )}
            </div>
          )}
        </div>
        <button onClick={onClose} className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
};

const VideoTestimonials = ({ limit, showHeader = true }) => {
  const { data: testimonials, loading } = useTestimonials();
  const [active, setActive] = useState(null);
  const list = limit ? (testimonials || []).slice(0, limit) : (testimonials || []);

  return (
    <section className="py-28 bg-slate-50/60" data-testid="video-testimonials-section">
      <div className="container-pn">
        {showHeader && (
          <div className="max-w-3xl mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Hear it from them</p>
            <h2 className="text-4xl md:text-5xl font-black text-[#050F2E] tracking-tight">
              Customer <span className="text-[#0A2A63]">video stories</span>
            </h2>
            <p className="mt-4 text-slate-600">Real printers on how ProofNation made their technology work for them. Press play.</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading testimonials...</div>
        ) : list.length === 0 ? (
          <p className="text-slate-500">No testimonials published yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((t) => (
              <VideoCard key={t.id} t={t} onPlay={setActive} />
            ))}
          </div>
        )}
      </div>
      <VideoModal t={active} onClose={() => setActive(null)} />
    </section>
  );
};

export default VideoTestimonials;
